import { ReactNode } from 'react';

type SelectOptionValue = string | number;
type SelectOptionLabel = ReactNode;
type SelectOptionAccessor<TItem, TResult> =
  | keyof TItem
  | ((item: TItem) => TResult);

interface FormatSelectOptionsConfig<
  TItem,
  TExtra extends Record<string, unknown>,
> {
  value: SelectOptionAccessor<TItem, SelectOptionValue | null | undefined>;
  label: SelectOptionAccessor<TItem, SelectOptionLabel | null | undefined>;
  extra?: (item: TItem) => TExtra;
  keepInvalid?: boolean;
}

export type SelectOption<TExtra extends Record<string, unknown>> = {
  value: SelectOptionValue;
  label: SelectOptionLabel;
} & TExtra;

function getFieldValue<TItem, TResult>(
  item: TItem,
  accessor: SelectOptionAccessor<TItem, TResult>
): TResult {
  if (typeof accessor === 'function') {
    return accessor(item);
  }

  return item[accessor] as TResult;
}

function isInvalidOptionData(
  value: SelectOptionValue | null | undefined,
  label: SelectOptionLabel | null | undefined
): boolean {
  if (value === undefined || value === null) {
    return true;
  }

  if (label === undefined || label === null) {
    return true;
  }

  if (typeof label === 'string' && label.trim().length === 0) {
    return true;
  }

  return false;
}

export function formatSelectOptions<
  TItem,
  TExtra extends Record<string, unknown> = Record<string, never>,
>(
  items: TItem[] | null | undefined,
  config: FormatSelectOptionsConfig<TItem, TExtra>
): SelectOption<TExtra>[] {
  const normalizedItems = items ?? [];

  return normalizedItems.reduce<SelectOption<TExtra>[]>((options, item) => {
    const value = getFieldValue(item, config.value);
    const label = getFieldValue(item, config.label);

    if (!config.keepInvalid && isInvalidOptionData(value, label)) {
      return options;
    }

    options.push({
      value: value as SelectOptionValue,
      label: label as SelectOptionLabel,
      ...(config.extra?.(item) ?? ({} as TExtra)),
    });

    return options;
  }, []);
}
